import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Users, Package, UserCheck, DollarSign, TrendingUp, AlertCircle, Lock, CalendarClock } from 'lucide-react';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, LineChart, Line } from 'recharts';
import UpcomingDueCustomers from './UpcomingDueCustomers';
import InstallmentSummaryCards from './InstallmentSummaryCards';
import GreetingHeader from './GreetingHeader';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function BranchManagerDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API_URL}/api/dashboard/branch-manager-stats`, { withCredentials: true })
      .then(({ data }) => setStats(data))
      .catch(() => setStats({}))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center h-96 text-slate-400">Loading dashboard...</div>;

  const cards = [
    { title: 'Customers', value: stats?.total_customers || 0, icon: Users, color: 'text-blue-500', testId: 'bm-stat-customers' },
    { title: 'Inventory Available', value: stats?.total_inventory || 0, icon: Package, color: 'text-purple-500', testId: 'bm-stat-inventory' },
    { title: 'Employees', value: stats?.total_employees || 0, icon: UserCheck, color: 'text-cyan-500', testId: 'bm-stat-employees' },
    { title: 'Active Installments', value: stats?.active_installments || 0, icon: CalendarClock, color: 'text-emerald-500', testId: 'bm-stat-active' },
    { title: 'Monthly Recoverable', value: `PKR ${(stats?.monthly_recoverable || 0).toLocaleString()}`, icon: DollarSign, color: 'text-amber-500', testId: 'bm-stat-recoverable' },
    { title: 'Monthly Pending Recovery', value: `PKR ${(stats?.monthly_pending_recovery || 0).toLocaleString()}`, icon: AlertCircle, color: 'text-orange-500', testId: 'bm-stat-monthly-pending-recovery' },
    { title: 'Total Recovered', value: `PKR ${(stats?.total_recovered || 0).toLocaleString()}`, icon: TrendingUp, color: 'text-emerald-500', testId: 'bm-stat-recovered' },
    { title: 'Outstanding', value: `PKR ${(stats?.outstanding || 0).toLocaleString()}`, icon: AlertCircle, color: 'text-yellow-500', testId: 'bm-stat-outstanding' },
    { title: 'Default Customers', value: stats?.default_customers || 0, icon: AlertCircle, color: 'text-red-500', testId: 'bm-stat-defaults' },
    { title: 'Pending Lock/Unlock', value: stats?.pending_lock_requests || 0, icon: Lock, color: 'text-orange-500', testId: 'bm-stat-lock' },
  ];

  const recoveryData = [
    { name: 'Recovered', value: Math.max(0, stats?.total_recovered || 0) },
    { name: 'Outstanding', value: Math.max(0, stats?.outstanding || 0) },
  ];
  const RECOVERY_COLORS = ['#10B981', '#F59E0B'];

  const portfolioData = [
    { name: 'Active', count: stats?.active_installments || 0 },
    { name: 'Default', count: stats?.default_customers || 0 },
    { name: 'Customers', count: stats?.total_customers || 0 },
    { name: 'Inventory', count: stats?.total_inventory || 0 },
    { name: 'Employees', count: stats?.total_employees || 0 },
    { name: 'Lock Req.', count: stats?.pending_lock_requests || 0 },
  ];

  return (
    <div className="space-y-6" data-testid="branch-manager-dashboard">
      <GreetingHeader
        testId="bm-greeting"
        fallbackName="Branch Manager"
        subtitle="Branch Manager Dashboard · Oversee branch operations, staff and recovery in one glance"
      />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c, i) => (
          <Card key={i} className="bg-slate-900 border-slate-800 hover:-translate-y-1 transition-transform" data-testid={c.testId}>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-slate-400">{c.title}</CardTitle>
              <c.icon className={`h-4 w-4 ${c.color}`} />
            </CardHeader>
            <CardContent><div className="text-2xl font-bold text-white">{c.value}</div></CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="bg-slate-900 border-slate-800" data-testid="bm-chart-recovery">
          <CardHeader><CardTitle className="text-white text-base">Recovered vs Outstanding</CardTitle></CardHeader>
          <CardContent style={{ height: 260 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={recoveryData} dataKey="value" nameKey="name" innerRadius={50} outerRadius={90} paddingAngle={2}>
                  {recoveryData.map((_, i) => <Cell key={i} fill={RECOVERY_COLORS[i]} />)}
                </Pie>
                <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #334155', color: '#fff' }} />
                <Legend wrapperStyle={{ color: '#cbd5e1', fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
        <Card className="bg-slate-900 border-slate-800" data-testid="bm-chart-portfolio">
          <CardHeader><CardTitle className="text-white text-base">Branch Portfolio</CardTitle></CardHeader>
          <CardContent style={{ height: 260 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={portfolioData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="name" stroke="#94a3b8" fontSize={11} />
                <YAxis stroke="#94a3b8" fontSize={11} />
                <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #334155', color: '#fff' }} />
                <Bar dataKey="count" fill="#F59E0B" radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      <Card className="bg-slate-900 border-slate-800" data-testid="bm-chart-trend">
        <CardHeader><CardTitle className="text-white text-base">30-Day Collection Trend</CardTitle></CardHeader>
        <CardContent style={{ height: 240 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={(stats?.trend_30d) || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis dataKey="date" stroke="#94a3b8" fontSize={10} />
              <YAxis stroke="#94a3b8" fontSize={11} />
              <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #334155', color: '#fff' }}
                formatter={(v) => `PKR ${Number(v).toLocaleString()}`} />
              <Line type="monotone" dataKey="amount" stroke="#10B981" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    <InstallmentSummaryCards />
      <UpcomingDueCustomers />

    </div>
  );
}
